import React, { useRef, useEffect, useState, useCallback } from 'react'
import { AutomationEnvelope, AutomationPoint, CurveType } from '@/utils/AutomationEnvelope'

interface AutomationEnvelopeOverlayProps {
  // 轨道信息
  trackIndex: number  // 0 或 1
  trackColor: string  // 轨道颜色
  
  // Envelope 数据
  envelope: AutomationEnvelope
  onEnvelopeChange?: (envelope: AutomationEnvelope) => void
  
  // 时间和尺寸信息
  containerWidth: number
  containerHeight: number
  duration: number      // 总时长（秒）
  trackOffset: number   // 轨道在容器中的垂直偏移（像素）
  
  // 转场区域
  transitionStart: number  // 转场开始时间（秒）
  transitionEnd: number    // 转场结束时间（秒）
  
  // 交互控制
  enabled: boolean
  showPoints?: boolean
  showCurve?: boolean
  
  // 垂直偏移（用于错开不同类型的 envelope）
  verticalShift?: number  // 0-1 之间，表示相对轨道高度的偏移比例
}

export const AutomationEnvelopeOverlay: React.FC<AutomationEnvelopeOverlayProps> = ({
  trackIndex,
  trackColor,
  envelope,
  onEnvelopeChange,
  containerWidth,
  containerHeight,
  duration,
  trackOffset,
  transitionStart,
  transitionEnd,
  enabled,
  showPoints = true,
  showCurve = true,
  verticalShift = 0,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  
  // 拖拽状态
  const [draggingPointIndex, setDraggingPointIndex] = useState<number>(-1)
  const [hoveredPointIndex, setHoveredPointIndex] = useState<number>(-1)
  
  // 时间到像素的转换
  const timeToX = useCallback((time: number): number => {
    return (time / duration) * containerWidth
  }, [duration, containerWidth])
  
  // 像素到时间的转换
  const xToTime = useCallback((x: number): number => {
    return (x / containerWidth) * duration
  }, [duration, containerWidth])
  
  // 值到Y坐标的转换（0-1 映射到 trackHeight 范围，反向）
  // 添加上下边距，使曲线不会触及轨道边界，并支持垂直偏移
  const valueToY = useCallback((value: number): number => {
    const trackHeight = containerHeight / 2  // 每个轨道占一半高度
    const baseMargin = trackHeight * 0.15  // 基础边距 15%
    const shiftAmount = trackHeight * verticalShift  // 垂直偏移量
    const margin = baseMargin + shiftAmount  // 顶部边距
    const bottomMargin = baseMargin - shiftAmount  // 底部边距（保持总高度不变）
    const usableHeight = trackHeight - margin - bottomMargin
    const y = trackOffset + margin + usableHeight * (1 - value)
    return y
  }, [containerHeight, trackOffset, verticalShift])
  
  // Y坐标到值的转换
  const yToValue = useCallback((y: number): number => {
    const trackHeight = containerHeight / 2
    const baseMargin = trackHeight * 0.15
    const shiftAmount = trackHeight * verticalShift
    const margin = baseMargin + shiftAmount
    const bottomMargin = baseMargin - shiftAmount
    const usableHeight = trackHeight - margin - bottomMargin
    const relativeY = y - trackOffset - margin
    const value = 1 - (relativeY / usableHeight)
    return Math.max(0, Math.min(1, value))
  }, [containerHeight, trackOffset, verticalShift])
  
  // 绘制 Canvas
  const draw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    
    // 清空画布
    ctx.clearRect(0, 0, containerWidth, containerHeight)
    
    // 只在转场区域内绘制
    const transitionStartX = timeToX(transitionStart)
    const transitionEndX = timeToX(transitionEnd)
    
    // 设置裁剪区域为转场范围
    ctx.save()
    ctx.beginPath()
    ctx.rect(transitionStartX, trackOffset, transitionEndX - transitionStartX, containerHeight / 2)
    ctx.clip()
    
    // 绘制曲线
    if (showCurve) {
      const curveData = envelope.generateCurve(200)
      
      if (curveData.length > 0) {
        ctx.beginPath()
        ctx.strokeStyle = trackColor
        ctx.lineWidth = 3
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        
        // 添加轻微的阴影
        ctx.shadowColor = trackColor
        ctx.shadowBlur = 4
        
        curveData.forEach((point, index) => {
          const x = timeToX(point.time)
          const y = valueToY(point.value)
          
          if (index === 0) {
            ctx.moveTo(x, y)
          } else {
            ctx.lineTo(x, y)
          }
        })
        
        ctx.stroke()
        ctx.shadowBlur = 0
      }
    }
    
    ctx.restore()
    
    // 绘制控制点
    if (showPoints) {
      const points = envelope.getPoints()
      
      points.forEach((point, index) => {
        const x = timeToX(point.time)
        const y = valueToY(point.value)
        
        // 只绘制在转场区域内的点
        if (x >= transitionStartX && x <= transitionEndX) {
          const isHovered = index === hoveredPointIndex
          const isDragging = index === draggingPointIndex
          
          // 外圈
          ctx.beginPath()
          ctx.arc(x, y, isDragging ? 10 : isHovered ? 8 : 6, 0, Math.PI * 2)
          ctx.fillStyle = trackColor
          ctx.fill()
          
          // 内圈
          ctx.beginPath()
          ctx.arc(x, y, isDragging ? 6 : isHovered ? 5 : 3, 0, Math.PI * 2)
          ctx.fillStyle = '#ffffff'
          ctx.fill()
          
          // 边框
          ctx.beginPath()
          ctx.arc(x, y, isDragging ? 10 : isHovered ? 8 : 6, 0, Math.PI * 2)
          ctx.strokeStyle = isDragging ? '#ffffff' : isHovered ? '#ffffff' : trackColor
          ctx.lineWidth = 2
          ctx.stroke()
        }
      })
    }
  }, [
    envelope,
    containerWidth,
    containerHeight,
    duration,
    trackOffset,
    transitionStart,
    transitionEnd,
    trackColor,
    showPoints,
    showCurve,
    hoveredPointIndex,
    draggingPointIndex,
    timeToX,
    valueToY,
  ])
  
  // 监听数据变化重新绘制
  useEffect(() => {
    draw()
  }, [draw])
  
  // 查找鼠标下的点
  const findPointAtPosition = useCallback((x: number, y: number): number => {
    const points = envelope.getPoints()
    const threshold = 15 // 点击阈值（像素）
    
    for (let i = 0; i < points.length; i++) {
      const point = points[i]
      const pointX = timeToX(point.time)
      const pointY = valueToY(point.value)
      
      const distance = Math.sqrt(
        Math.pow(x - pointX, 2) + Math.pow(y - pointY, 2)
      )
      
      if (distance <= threshold) {
        return i
      }
    }
    
    return -1
  }, [envelope, timeToX, valueToY])
  
  // 鼠标移动处理
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!enabled) return
    
    const canvas = canvasRef.current
    if (!canvas) return
    
    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    
    if (draggingPointIndex >= 0) {
      // 拖拽点
      const newTime = xToTime(x)
      const newValue = yToValue(y)
      
      // 限制在转场区域内
      const clampedTime = Math.max(transitionStart, Math.min(transitionEnd, newTime))
      
      envelope.updatePoint(draggingPointIndex, {
        time: clampedTime,
        value: newValue,
      })
      
      if (onEnvelopeChange) {
        onEnvelopeChange(envelope)
      }
      
      draw()
    } else {
      // 检测悬停
      const pointIndex = findPointAtPosition(x, y)
      if (pointIndex !== hoveredPointIndex) {
        setHoveredPointIndex(pointIndex)
        canvas.style.cursor = pointIndex >= 0 ? 'grab' : 'crosshair'
      }
    }
  }, [
    enabled,
    draggingPointIndex,
    hoveredPointIndex,
    envelope,
    onEnvelopeChange,
    xToTime,
    yToValue,
    transitionStart,
    transitionEnd,
    findPointAtPosition,
    draw,
  ])
  
  // 鼠标按下处理
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!enabled) return
    
    const canvas = canvasRef.current
    if (!canvas) return
    
    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    
    const pointIndex = findPointAtPosition(x, y)
    
    if (pointIndex >= 0) {
      // 开始拖拽已存在的点
      setDraggingPointIndex(pointIndex)
      canvas.style.cursor = 'grabbing'
    } else {
      // 添加新点（仅在转场区域内）
      const time = xToTime(x)
      if (time >= transitionStart && time <= transitionEnd) {
        const value = yToValue(y)
        envelope.addPoint({
          time,
          value,
          curveType: CurveType.SMOOTH,
        })
        
        if (onEnvelopeChange) {
          onEnvelopeChange(envelope)
        }
        
        // 立即开始拖拽新添加的点
        const newPointIndex = envelope.getPoints().findIndex(p => 
          Math.abs(p.time - time) < 0.01 && Math.abs(p.value - value) < 0.01
        )
        setDraggingPointIndex(newPointIndex)
        
        draw()
      }
    }
  }, [
    enabled,
    envelope,
    onEnvelopeChange,
    xToTime,
    yToValue,
    transitionStart,
    transitionEnd,
    findPointAtPosition,
    draw,
  ])
  
  // 鼠标松开处理
  const handleMouseUp = useCallback(() => {
    if (draggingPointIndex >= 0) {
      setDraggingPointIndex(-1)
      const canvas = canvasRef.current
      if (canvas) {
        canvas.style.cursor = 'crosshair'
      }
    }
  }, [draggingPointIndex])
  
  // 鼠标离开处理
  const handleMouseLeave = useCallback(() => {
    setDraggingPointIndex(-1)
    setHoveredPointIndex(-1)
    const canvas = canvasRef.current
    if (canvas) {
      canvas.style.cursor = 'default'
    }
  }, [])
  
  // 双击切换曲线类型
  const handleDoubleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!enabled) return
    
    const canvas = canvasRef.current
    if (!canvas) return
    
    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    
    const pointIndex = findPointAtPosition(x, y)
    
    if (pointIndex >= 0) {
      const points = envelope.getPoints()
      const currentPoint = points[pointIndex]
      
      // 循环切换曲线类型
      const curveTypes = [
        CurveType.LINEAR,
        CurveType.SMOOTH,
        CurveType.EXPONENTIAL,
        CurveType.LOGARITHMIC,
        CurveType.STEP,
      ]
      
      const currentIndex = curveTypes.indexOf(currentPoint.curveType)
      const nextIndex = (currentIndex + 1) % curveTypes.length
      const nextCurveType = curveTypes[nextIndex]
      
      envelope.updatePoint(pointIndex, { curveType: nextCurveType })
      
      if (onEnvelopeChange) {
        onEnvelopeChange(envelope)
      }
      
      // 显示提示
      console.log(`🎨 Point ${pointIndex} curve type changed to: ${nextCurveType}`)
      
      draw()
    }
  }, [enabled, envelope, onEnvelopeChange, findPointAtPosition, draw])
  
  // 右键删除点
  const handleContextMenu = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault()
    
    if (!enabled) return
    
    const canvas = canvasRef.current
    if (!canvas) return
    
    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    
    const pointIndex = findPointAtPosition(x, y)
    
    if (pointIndex >= 0) {
      envelope.removePoint(pointIndex)
      
      if (onEnvelopeChange) {
        onEnvelopeChange(envelope)
      }
      
      draw()
    }
  }, [enabled, envelope, onEnvelopeChange, findPointAtPosition, draw])
  
  return (
    <canvas
      ref={canvasRef}
      width={containerWidth}
      height={containerHeight}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: enabled ? 'auto' : 'none',
        cursor: enabled ? 'crosshair' : 'default',
      }}
      onMouseMove={handleMouseMove}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
    />
  )
}

